/**
 * Renders resolved specific complementary accessories on the PDP.
 */

(function complementaryAccessoriesUi() {
  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatMoney(cents, moneyFormat) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && moneyFormat) {
      return Shopify.formatMoney(cents, moneyFormat);
    }

    return (Number(cents) / 100).toFixed(2);
  }

  function formatFromPrice(cents, strings, moneyFormat) {
    const formatted = formatMoney(cents, moneyFormat);
    const template = strings.fromPriceHtml || 'From __PRICE__';

    return template.replace('__PRICE__', formatted).replace(/\{\{\s*price\s*\}\}/, formatted);
  }

  function decorateGroups(groups, catalog) {
    const moneyFormat = catalog.moneyFormat;

    return groups.map((group) => ({
      ...group,
      minPriceHtml: group.showPriceRange
        ? formatFromPrice(group.minPrice, catalog.strings ?? {}, moneyFormat)
        : null,
    }));
  }

  function getRatioPercent(mediaAspectRatio, imageAspectRatio) {
    let ratio = 1;

    if (mediaAspectRatio === 'portrait') {
      ratio = 0.8;
    } else if (mediaAspectRatio === 'adapt' && imageAspectRatio) {
      ratio = imageAspectRatio;
    }

    if (!ratio) ratio = 1;

    return `${(1 / ratio) * 100}%`;
  }

  function buildCardTitle(product, primaryVariant, variantCount) {
    let title = product.title ?? '';

    if (
      variantCount === 1 &&
      primaryVariant?.title &&
      primaryVariant.title !== 'Default Title'
    ) {
      title = `${title} – ${primaryVariant.title}`;
    }

    return title;
  }

  function buildProductUrl(product, primaryVariant, group) {
    let url = `${product.url}?variant=${primaryVariant.id}`;

    if (group.lockSelection) {
      if (group.variantCount > 1 && group.allowedVariantIds) {
        url += `&allowed_variants=${group.allowedVariantIds}&locked=1`;
      } else {
        url += '&locked=1';
      }
    }

    return url;
  }

  function renderPrice(group, strings) {
    if (group.showPriceRange && group.minPriceHtml) {
      return `
        <div class="price">
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">${escapeHtml(strings.regularPriceLabel)}</span>
              <span class="price-item price-item--regular">${group.minPriceHtml}</span>
            </div>
          </div>
        </div>
      `;
    }

    return `
      <div class="price">
        <div class="price__container">
          <div class="price__regular">
            <span class="price-item price-item--regular">${group.primaryVariant.priceHtml ?? ''}</span>
          </div>
        </div>
      </div>
    `;
  }

  function renderCard(group, config, strings, spinnerHtml) {
    const { product, primaryVariant, variantCount } = group;
    const title = buildCardTitle(product, primaryVariant, variantCount);
    const ratioPercent = getRatioPercent(config.mediaAspectRatio, product.imageAspectRatio);
    const cardUrl = buildProductUrl(product, primaryVariant, group);
    const linkId = `ComplementaryVariantLink-${config.sectionId}-${primaryVariant.id}`;

    const imageMarkup = product.imageUrl
      ? `
        <div class="card__media">
          <div class="media media--transparent media--hover-effect">
            <img
              src="${escapeHtml(product.imageUrl)}"
              alt="${escapeHtml(title)}"
              loading="lazy"
              class="motion-reduce"
              sizes="13rem"
            >
          </div>
        </div>
      `
      : '';

    const badgeMarkup =
      variantCount > 1
        ? `<span class="complementary-variant-card__badge badge">${escapeHtml(strings.variantBadgeLabel)}</span>`
        : '';

    return `
      <li class="grid__item slider__slide" id="Slide-${config.sliderId}-${primaryVariant.id}">
        <button
          type="button"
          class="complementary-variant-card__opener card-wrapper product-card-wrapper underline-links-hover"
          data-complementary-variant-opener
          data-product-url="${escapeHtml(cardUrl)}"
          aria-labelledby="${linkId}"
        >
          <div class="card card--standard card--media" style="--ratio-percent: ${ratioPercent};">
            <div class="card__inner ratio" style="--ratio-percent: ${ratioPercent};">
              ${imageMarkup}
              ${badgeMarkup}
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <span class="complementary-variant-card__title" id="${linkId}">${escapeHtml(title)}</span>
                </h3>
                <div class="card-information">
                  ${renderPrice(group, strings)}
                </div>
              </div>
            </div>
          </div>
          ${spinnerHtml}
        </button>
      </li>
    `;
  }

  function renderSliderControls(sliderId, controlsTemplate) {
    if (controlsTemplate) {
      return controlsTemplate.replaceAll('__SLIDER_ID__', sliderId);
    }

    return `
      <div class="product-complementary-slider__buttons no-js-hidden">
        <button
          type="button"
          class="slider-button slider-button--prev product-complementary-slider__button product-complementary-slider__button--prev"
          name="previous"
          aria-label="Previous"
          aria-controls="Slider-${sliderId}"
        ></button>
        <button
          type="button"
          class="slider-button slider-button--next product-complementary-slider__button product-complementary-slider__button--next"
          name="next"
          aria-label="Next"
          aria-controls="Slider-${sliderId}"
        ></button>
      </div>
    `;
  }

  function renderGrid(groups, config, strings, spinnerHtml, controlsTemplate) {
    if (!groups.length) return '';

    const configuredLimit = Number(config.limit);
    const limit =
      Number.isFinite(configuredLimit) && configuredLimit > 0 ? configuredLimit : groups.length;
    const visibleGroups = groups.slice(0, limit);
    const enableSlider = visibleGroups.length > config.visibleColumns;
    const cards = visibleGroups
      .map((group) => renderCard(group, config, strings, spinnerHtml))
      .join('');

    if (!enableSlider) {
      return `
        <ul class="grid product-grid grid--${config.columnsDesktop}-col-desktop grid--${config.columnsMobile}-col-tablet-down" role="list">
          ${cards}
        </ul>
      `;
    }

    return `
      <slider-component class="product-complementary-slider slider-mobile-gutter">
        <div class="product-complementary-slider__viewport">
          <ul
            id="Slider-${config.sliderId}"
            class="grid product-grid grid--${config.columnsDesktop}-col-desktop grid--${config.columnsMobile}-col-tablet-down slider slider--everywhere contains-card contains-card--product"
            role="list"
          >
            ${cards}
          </ul>
          ${renderSliderControls(config.sliderId, controlsTemplate)}
        </div>
      </slider-component>
    `;
  }

  function refreshComplementarySlider(container) {
    if (!container) return;

    requestAnimationFrame(() => {
      container.querySelectorAll('slider-component').forEach((slider) => {
        if (typeof slider.resetPages === 'function') {
          slider.resetPages();
        }
      });
    });
  }

  function setPanelVisible(root, visible) {
    const panel = root.querySelector('[data-complementary-variants-panel]');
    const aside = root.querySelector('[data-product-complementary-variants]');

    if (panel) {
      panel.hidden = !visible;
    }

    if (aside) {
      aside.hidden = !visible;
    }
  }

  function initController(root) {
    const catalogNode = root.querySelector('[data-complementary-accessories-catalog]');
    const target = root.querySelector('[data-complementary-variants-current]');
    const contextValue = root.querySelector('[data-complementary-variants-context-value]');
    const spinnerTemplate = root.querySelector('[data-complementary-card-spinner-template]');
    const controlsTemplate = root.querySelector('[data-complementary-slider-controls-template]');

    if (!catalogNode || !target) return null;

    let catalog;
    try {
      catalog = JSON.parse(catalogNode.textContent);
    } catch (error) {
      console.error('Complementary accessories catalog parse failed', error);
      return null;
    }

    if (!window.ComplementaryProductsResolver?.catalogUsesNewResolver(catalog)) {
      return null;
    }

    const config = catalog.renderConfig ?? {};
    const strings = catalog.strings ?? {};
    const spinnerHtml = spinnerTemplate ? spinnerTemplate.innerHTML : '';
    const sliderControlsHtml = controlsTemplate ? controlsTemplate.innerHTML : '';
    const mainVariantsById = catalog.mainVariantsById ?? {};

    const update = (mainVariantId) => {
      const mainVariant = mainVariantsById[String(mainVariantId)];
      const label = mainVariant?.label ?? '';

      if (!mainVariant) {
        setPanelVisible(root, false);
        target.innerHTML = '';
        if (contextValue) contextValue.textContent = '';
        return;
      }

      const groups = decorateGroups(
        window.ComplementaryProductsResolver.resolveSpecificComplementaryGroups(mainVariant, catalog),
        catalog
      );

      if (!groups.length) {
        setPanelVisible(root, false);
        target.innerHTML = '';
        if (contextValue) contextValue.textContent = label;
        return;
      }

      target.innerHTML = renderGrid(groups, config, strings, spinnerHtml, sliderControlsHtml);
      if (contextValue) contextValue.textContent = label;
      setPanelVisible(root, true);
      refreshComplementarySlider(target);
    };

    return { update, catalog, config };
  }

  function bindVariantChanges(section, controller, mainProductId) {
    if (!controller || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      return;
    }

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data?.fromQuickAddModal) return;
      if (event.data?.productId && String(event.data.productId) !== String(mainProductId)) return;

      controller.update(event.data.variant?.id);
    });
  }

  function boot() {
    document.querySelectorAll('[data-complementary-accessories-root]').forEach((root) => {
      if (root.dataset.complementaryAccessoriesReady === 'true') return;

      const controller = initController(root);
      if (!controller) return;

      root.dataset.complementaryAccessoriesReady = 'true';
      bindVariantChanges(root.closest('[id^="shopify-section-"]'), controller, controller.catalog.mainProductId);
      controller.update(controller.config.initialVariantId);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.ComplementaryAccessoriesUi = {
    initController,
    renderGrid,
    refreshComplementarySlider,
  };
})();
